import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { teacherService } from '@/services/teacher';
import { resolveAssetUrl } from '@/utils/assetUrl';
import { generateStrongPassword } from '@/utils/password';

export default function TeacherProfilePage() {
  const queryClient = useQueryClient();
  const { data: profile, isLoading, isError } = useQuery({
    queryKey: ['teacher-profile'],
    queryFn: () => teacherService.getProfile(),
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [copiedCurrentPassword, setCopiedCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const passwordMutation = useMutation({
    mutationFn: (password: string) => teacherService.updatePassword(password),
    onSuccess: (data) => {
      setStatusMessage({ type: 'success', text: data.message || 'Password updated successfully.' });
      setNewPassword('');
      setConfirmPassword('');
      queryClient.invalidateQueries({ queryKey: ['teacher-profile'] });
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update password.';
      setStatusMessage({ type: 'error', text: msg });
    },
  });

  const handleGenerate = () => {
    const generated = generateStrongPassword(16);
    setNewPassword(generated);
    setConfirmPassword(generated);
    setShowNewPassword(true);
    setShowConfirmPassword(true);
    setStatusMessage(null);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null);

    if (!newPassword) {
      setStatusMessage({ type: 'error', text: 'Please enter a new password.' });
      return;
    }

    if (newPassword.length < 12) {
      setStatusMessage({ type: 'error', text: 'Password must be at least 12 characters long.' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatusMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    passwordMutation.mutate(newPassword);
  };

  if (isLoading) {
    return (
      <DashboardShell title="Faculty Profile" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-xs text-center">
          <p className="text-sm font-medium text-slate-500">Loading faculty profile…</p>
        </div>
      </DashboardShell>
    );
  }

  if (isError || !profile) {
    return (
      <DashboardShell title="Faculty Profile" role="teacher">
        <div className="bg-white p-8 rounded-2xl border border-rose-100 shadow-xs text-center space-y-2">
          <p className="text-sm font-bold text-rose-600">Failed to load profile.</p>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title="Faculty Profile" role="teacher">
      <div className="space-y-6 pb-12">
        <div className="grid gap-6 lg:grid-cols-12">
          {/* Left Column: Avatar & Contact Card (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col items-center text-center space-y-4">
              <div className="h-28 w-28 overflow-hidden rounded-2xl border-2 border-indigo-100 shadow-xs bg-indigo-50 flex items-center justify-center">
                {profile.photo ? (
                  <img
                    src={resolveAssetUrl(profile.photo) || undefined}
                    alt={profile.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-3xl font-black text-indigo-700">
                    {profile.name?.charAt(0) || 'T'}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                <h2 className="text-lg font-black text-slate-900">{profile.name}</h2>
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                  Faculty Member
                </div>
              </div>

              <div className="w-full border-t border-slate-100 pt-4 space-y-3 text-left text-xs">
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Phone</span>
                  <span className="font-semibold text-slate-800">{profile.phone || '—'}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider">Address</span>
                  <span className="font-semibold text-slate-800">{profile.address || '—'}</span>
                </div>
              </div>
            </div>

            {/* Qualifications Card */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-2">
              <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Credentials</span>
              <h3 className="text-sm font-bold text-slate-900">Academic Qualifications</h3>
              <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed pt-1">
                {profile.qualifications || 'No qualifications listed on record.'}
              </p>
            </div>
          </div>

          {/* Right Column: Security, Password & Assigned Classes (8 cols) */}
          <div className="lg:col-span-8 space-y-6">
            {/* Login & Security Card */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Access Control</span>
                  <h3 className="text-base font-bold text-slate-900">Login & Security Credentials</h3>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1">
                  <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Email (Username)
                  </span>
                  <span className="text-xs font-bold text-slate-900 break-all font-mono">{profile.email}</span>
                </div>

                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Current Password
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((p) => !p)}
                        className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800"
                      >
                        {showCurrentPassword ? 'Hide' : 'Show'}
                      </button>
                      {profile.loginPassword && (
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(profile.loginPassword!);
                            setCopiedCurrentPassword(true);
                            setTimeout(() => setCopiedCurrentPassword(false), 2000);
                          }}
                          className="text-[11px] font-bold text-slate-600 hover:text-slate-900"
                        >
                          {copiedCurrentPassword ? '✓ Copied' : 'Copy'}
                        </button>
                      )}
                    </div>
                  </div>
                  <span className="font-mono text-xs font-bold text-slate-900 break-all select-all block">
                    {showCurrentPassword ? (profile.loginPassword || '—') : '••••••••••••'}
                  </span>
                </div>
              </div>

              {/* Change Password Sub-section */}
              <div className="border-t border-slate-100 pt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Change Password</h4>
                  <button
                    type="button"
                    onClick={handleGenerate}
                    className="text-xs font-bold text-indigo-600 hover:text-indigo-800"
                  >
                    ✨ Generate Strong Password
                  </button>
                </div>

                {statusMessage && (
                  <div
                    className={clsx(
                      'p-3.5 rounded-xl text-xs font-semibold border',
                      statusMessage.type === 'success'
                        ? 'bg-teal-50 text-teal-800 border-teal-200'
                        : 'bg-rose-50 text-rose-800 border-rose-200',
                    )}
                  >
                    {statusMessage.text}
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">
                        New Password (min 12 chars)
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          required
                          minLength={12}
                          placeholder="At least 12 characters"
                          className="w-full bg-slate-50 rounded-xl border border-slate-200 px-3.5 py-2 pr-10 text-base sm:text-xs font-mono min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((p) => !p)}
                          className="absolute inset-y-0 right-2 flex items-center text-xs font-bold text-slate-400 hover:text-slate-600 px-2"
                        >
                          {showNewPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          minLength={12}
                          placeholder="Re-enter new password"
                          className="w-full bg-slate-50 rounded-xl border border-slate-200 px-3.5 py-2 pr-10 text-base sm:text-xs font-mono min-h-[40px] focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((p) => !p)}
                          className="absolute inset-y-0 right-2 flex items-center text-xs font-bold text-slate-400 hover:text-slate-600 px-2"
                        >
                          {showConfirmPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-1">
                    <button
                      type="submit"
                      disabled={passwordMutation.isPending || !newPassword || !confirmPassword}
                      className="w-full sm:w-auto px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition active:scale-95 disabled:opacity-50 min-h-[42px]"
                    >
                      {passwordMutation.isPending ? 'Updating Password…' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Assigned Classes */}
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Teaching Scope</span>
                  <h3 className="text-sm font-bold text-slate-900">Assigned Homeroom Classes</h3>
                </div>
              </div>

              {profile.assignedClasses?.length ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  {profile.assignedClasses.map((c: any) => (
                    <div
                      key={c.id}
                      className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-bold text-sm text-slate-900">{c.name}</div>
                        <div className="text-[11px] text-slate-400 font-medium">Class Homeroom</div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {c.studentCount} Students
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-4 text-center">
                  No homeroom classes assigned yet. Contact school administration.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

